<template>
    <div class="tiles-grid-arranger">
        <!-- Controls -->
        <v-row>
            <v-col sm="12" md="3">
                <!-- Filmstrip (drop target for removing tiles from grid) -->
                <h2 class="text-body-1">Source images</h2>
                <p class="text-body-2 grey--text">Drag your chosen words and images into the mood board</p>
                <div class="filmstrip" @dragover.prevent @drop="onDropToFilmstrip">
                    <div v-for="tile in filmstripTiles" :key="tile.id" class="filmstrip-tile"
                        :class="{ 'filmstrip-tile--placed': tile.isPlaced }"
                        :style="{ width: thumbSize + 'px', height: thumbSize + 'px' }" draggable="true"
                        @dragstart="onFilmstripDragStart(tile, $event)">
                        <show-card :card="tile" :cellSize="thumbSize" />
                    </div>
                </div>
                <div class="my-4">
                    <v-btn small outlined @click="resetGrid">
                        Reset tile positions
                    </v-btn>

                    <v-row class="mt-4">
                        <v-col cols="12">
                            <div class="d-flex justify-space-between align-center">
                                <div class="flex-grow-1">
                                    <p class="subtitle-2 my-0">Columns</p>
                                    <v-slider v-model="cols" :min="1" :max="maxCols" step="1" ticks dense
                                        color="amber"></v-slider>
                                </div>
                                <div class="flex-grow-1">
                                    <p class="subtitle-2 my-0">Zoom</p>
                                    <v-slider v-model="cellSize" :min="70" :max="300" step="10" ticks dense
                                        color="amber"></v-slider>
                                </div>
                            </div>
                        </v-col>
                    </v-row>
                </div>
            </v-col>
            <v-col sm="12" md="9">
                <h2 class="text-body-1">Mood board</h2>
                <p class="text-body-2 grey--text">Arrange your words and images</p>


                <div class="grid-wrapper" ref="gridWrapper">
                    <div class="grid" :style="{
                        gridTemplateColumns: 'repeat(' + cols + ', ' + cellSize + 'px)',
                        gridAutoRows: cellSize + 'px'
                    }">
                        <div v-for="(slot, index) in gridSlots" :key="index" class="grid-cell" :style="{
                            width: cellSize + 'px',
                            height: cellSize + 'px'
                        }" @dragover.prevent @drop="onDrop(index)">
                            <div v-if="slot" class="grid-cell__tile" draggable="true"
                                @dragstart="onDragStart(slot, 'grid', index)">
                                <show-card :card="slot" :cellSize="cellSize" />
                            </div>
                            <div v-else class="grid-cell__placeholder">
                                Drop tile here
                            </div>
                        </div>
                    </div>
                </div>

            </v-col>
        </v-row>



    </div>

</template>

<script>
import ShowCard from "@/components/core/ShowCard.vue";

export default {
    name: "TilesGridArranger",
    components: {
        ShowCard,
    },
    props: {
        tiles: {
            type: Array,
            required: true,
        },
        value: {
            type: Array,
            default: () => [],
        },
        initialCellSize: {
            type: Number,
            default: null,
        },
        initialCols: {
            type: Number,
            default: null,
        },
    },
    data() {
        return {
            cols: this.initialCols || 4,
            cellSize: this.initialCellSize || 190,
            thumbSize: 62,
            minCellSize: 60,
            maxCellSize: 300,
            internalSlots: [],
            dragState: null,
            hasAutoInitialised: false,
            userTouchedGrid: false,
        };
    },
    mounted() {
        console.log('mounted');
        if (!this.initialCellSize) {
            this.autoFitCellSize();
        }
    },
    computed: {
        maxCols() {
            return 8;
        },
        rows() {
            const slotsCount = this.internalSlots?.length || 0;
            const tilesCount = this.tiles?.length || 0;

            const count = Math.max(slotsCount, tilesCount, 1);

            if (!this.cols) return 1;

            return Math.ceil(count / this.cols);
        },
        gridSlots() {
            console.log('rows', this.rows)
            const total = this.rows * this.cols;
            const slots = this.internalSlots.slice(0, total);
            while (slots.length < total) {
                slots.push(null);
            }
            return slots;
        },
        placedIds() {
            return new Set(
                this.gridSlots
                    .filter((t) => t && t.id)
                    .map((t) => t.id)
            );
        },
        filmstripTiles() {
            return this.tiles.map((tile) => ({
                ...tile,
                isPlaced: this.placedIds.has(tile.id),
            }));
        },
        placedCount() {
            return this.gridSlots.filter((t) => t).length;
        },
    },
    watch: {
        value: {
            immediate: true,
            handler(val) {
                if (Array.isArray(val) && val.length) {
                    this.internalSlots = val.slice();
                    this.hasAutoInitialised = true;
                }
            },
        },

        // 🔹 UPDATED: keep internalSlots in sync with tiles (add/remove) + auto-seed
        tiles: {
            immediate: true,
            handler(tiles) {
                tiles = tiles || [];

                // 1) Always prune removed tiles from the grid
                const ids = new Set(tiles.map((t) => t.id));
                const pruned = this.internalSlots.filter(
                    (slot) => slot && ids.has(slot.id)
                );
                if (pruned.length !== this.internalSlots.length) {
                    this.internalSlots = pruned;
                    this.syncToParent();
                }

                // If nothing left, nothing more to do
                if (!tiles.length) return;

                // If parent has a saved layout, don't auto-populate
                if (this.value && this.value.length) return;

                // If user has dragged / reset, don't auto-populate on add
                if (this.userTouchedGrid) return;

                // If we already have all tiles placed and have auto-init’d, do nothing
                if (this.internalSlots.length >= tiles.length && this.hasAutoInitialised) {
                    return;
                }

                // Decide sensible default cols
                if (this.initialCols != null) {
                    this.cols = Math.max(1, Math.min(this.initialCols, this.maxCols));
                } else {
                    const len = tiles.length;
                    let cols;
                    if (len <= 4) cols = 2;
                    else if (len <= 6) cols = 3;
                    else if (len <= 12) cols = 4;
                    else cols = 5;
                    this.cols = Math.max(1, Math.min(cols, this.maxCols));
                }

                // Auto-fill with words then images
                this.autoPopulateSequential();
                this.hasAutoInitialised = true;
                this.syncToParent();
                this.autoFitCellSizeOnFirstLoad();
            },
        },

        cols() {
            this.emitCellConfig();
        },
        cellSize() {
            this.emitCellConfig();
        },
    },
    methods: {
        syncToParent() {
            this.$emit("input", this.internalSlots.slice());
        },

        emitCellConfig() {
            this.$emit("update-cell-config", {
                cols: this.cols,
                cellSize: this.cellSize,
            });
        },

        autoFitCellSizeOnFirstLoad() {
            if (this.initialCellSize != null) return;
            if (this.value && this.value.length) return;
            this.autoFitCellSize();
        },

        autoFitCellSize() {
            this.$nextTick(() => {
                const wrapper = this.$refs.gridWrapper;
                if (!wrapper) return;

                const wrapperWidth = wrapper.clientWidth;
                if (!wrapperWidth) return;

                const gap = 8;
                const totalGaps = (this.cols - 1) * gap;

                let size = Math.floor((wrapperWidth - totalGaps) / this.cols);
                size = Math.max(this.minCellSize, Math.min(size, this.maxCellSize));

                this.cellSize = size;
                this.thumbSize = Math.max(48, Math.floor(size * 0.6));
            });
        },

        isImageTile(tile) {
            return tile.type === "Image";
        },
        isWordTile(tile) {
            return !this.isImageTile(tile);
        },

        autoPopulateSequential() {
            if (!this.tiles || !this.tiles.length) return;

            const words = this.tiles.filter((t) => this.isWordTile(t));
            const images = this.tiles.filter((t) => this.isImageTile(t));
            const ordered = [...words, ...images];

            this.internalSlots = ordered.slice();
        },

        onDragStart(tile, origin, fromIndex) {
            this.userTouchedGrid = true;
            this.dragState = {
                origin,
                tileId: tile.id,
                fromIndex,
            };
        },

        onFilmstripDragStart(tile, event) {
            if (this.placedIds.has(tile.id)) {
                event.preventDefault();
                this.dragState = null;
                return;
            }
            this.onDragStart(tile, "filmstrip", null);
        },

        onDrop(targetIndex) {
            if (!this.dragState) return;

            this.userTouchedGrid = true;

            const { origin, tileId, fromIndex } = this.dragState;
            const slots = this.gridSlots.slice();
            const draggedTile = this.tiles.find((t) => t.id === tileId);
            if (!draggedTile) {
                this.dragState = null;
                return;
            }

            const targetTile = slots[targetIndex] || null;

            if (origin === "filmstrip") {
                const alreadyPlaced = slots.some((t) => t && t.id === tileId);
                if (alreadyPlaced) {
                    this.dragState = null;
                    return;
                }
                slots[targetIndex] = draggedTile;
            } else if (origin === "grid") {
                if (fromIndex === null || fromIndex === undefined) {
                    this.dragState = null;
                    return;
                }
                if (fromIndex === targetIndex) {
                    this.dragState = null;
                    return;
                }

                const fromTile = slots[fromIndex];

                if (!targetTile) {
                    slots[targetIndex] = fromTile;
                    slots[fromIndex] = null;
                } else {
                    slots[targetIndex] = fromTile;
                    slots[fromIndex] = targetTile;
                }
            }

            this.internalSlots = slots;
            this.dragState = null;
            this.syncToParent();
        },

        onDropToFilmstrip() {
            if (!this.dragState) return;

            this.userTouchedGrid = true;

            const { origin, fromIndex } = this.dragState;
            if (origin === "grid" && fromIndex !== null && fromIndex !== undefined) {
                const slots = this.gridSlots.slice();
                slots[fromIndex] = null;
                this.internalSlots = slots;
                this.syncToParent();
            }

            this.dragState = null;
        },

        resetGrid() {
            this.internalSlots = [];
            this.dragState = null;
            this.userTouchedGrid = false;
            this.hasAutoInitialised = false;

            if (this.initialCols != null) this.cols = this.initialCols;
            if (this.initialCellSize != null) {
                this.cellSize = this.initialCellSize;
                this.thumbSize = this.initialCellSize * 0.6;
            }

            this.syncToParent();
            this.autoPopulateSequential();
        },
    },
};
</script>




<style scoped>
.tiles-grid-arranger {
    width: 100%;
    min-height: 70vh;
}

.filmstrip {
    display: flex;
    overflow-y: auto;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 1px;
    position: relative;
    width: 100%;
    background: black;
    z-index: 99;
    padding: 1rem;
    align-items: flex-start;
    justify-content: flex-start;
}

.filmstrip-tile {
    flex: 0 0 auto;
    margin-right: 8px;
    cursor: grab;
    transition: opacity 0.2s ease;
}

.filmstrip-tile h2 {
    font-size: 0.7rem;
}

.filmstrip-tile--placed {
    opacity: 0.3;
    filter: grayscale(1);
}

.grid-wrapper {
    display: flex;
    justify-content: flex-start;
}

.grid {
    display: grid;
    grid-gap: 8px;
}

.grid-cell {
    border: 1px dashed rgba(255, 255, 255, 0.2);
    display: flex;
    align-items: center;
    justify-content: center;
}

.grid-cell__tile {
    width: 100%;
    height: 100%;
    cursor: grab;
}

.grid-cell__placeholder {
    font-size: 12px;
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 4px;
    text-align: center;
}
</style>
